import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ReplyCreditPacks } from "@/components/ReplyCreditPacks";
import { CompanySlotPacks } from "@/components/CompanySlotPacks";
import { CheckCircle2, Crown, Loader2, Minus, Zap, Building2 } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function priceLabel(plan) {
  if (!plan.price) return { amount: "₹0", suffix: null };
  return { amount: `₹${plan.price.toLocaleString("en-IN")}`, suffix: plan.billing_cycle === "monthly" ? "/ month" : null };
}

function appsLabel(plan) {
  if (plan.application_limit == null) return "Unlimited applications";
  if (plan.billing_cycle === "monthly") return `${plan.application_limit} applications / month`;
  return `${plan.application_limit} applications included`;
}

function repliesLabel(plan) {
  if (plan.reply_limit == null) return "Unlimited replies";
  return `${plan.reply_limit} replies / month`;
}

function companiesLabel(plan) {
  if (plan.company_limit == null) return "Unlimited saved companies";
  return `Save up to ${plan.company_limit} companies`;
}

// Card bullet list: apps + replies + companies lines + "Everything in <prev>" + features new vs previous plan.
function cardBullets(plan, index, plans, featureMap) {
  const bullets = [appsLabel(plan), repliesLabel(plan), companiesLabel(plan)];
  const prev = index > 0 ? plans[index - 1] : null;
  if (prev) bullets.push(`Everything in ${prev.name}`);
  const trueKeys = featureMap.filter((f) => plan.features[f.key]);
  const shown = prev ? trueKeys.filter((f) => !prev.features[f.key]) : trueKeys;
  shown.forEach((f) => bullets.push(f.label));
  return bullets;
}

export default function Pricing() {
  const { user, quota, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [current, setCurrent] = useState("free");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [replyCfg, setReplyCfg] = useState(null);
  const [companyCfg, setCompanyCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/plans");
      setPlans(data.items);
      setFeatures(data.features);
      setCurrent(data.current_plan);
    } catch {
      toast.error("Could not load plans.");
    } finally {
      setLoading(false);
    }
    api.get("/payments/reply-credits/config").then(({ data }) => setReplyCfg(data)).catch(() => {});
    api.get("/payments/company-slots/config").then(({ data }) => setCompanyCfg(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const finalize = async (payload, planName) => {
    await api.post("/payments/verify", payload);
    await refreshUser();
    setCurrent(payload._slug);
    toast.success(`You're now on the ${planName} plan!`);
  };

  const choose = async (plan) => {
    if (plan.slug === "free") {
      toast.info(current === "free" ? "You're on the Free plan." : "The Free plan is always available.");
      return;
    }
    setBusy(plan.id);
    try {
      const { data: order } = await api.post("/payments/create-order", { plan_id: plan.id });
      if (order.mock_mode || !order.key_id) {
        await finalize({ order_id: order.order_id, _slug: plan.slug }, plan.name);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay checkout."); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "JobBasket.Network",
        description: `${plan.name} Plan`,
        order_id: order.order_id,
        prefill: { email: user?.email, name: user?.name },
        theme: { color: "#f59e0b" },
        handler: async (res) => {
          try {
            await finalize({
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
              _slug: plan.slug,
            }, plan.name);
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  }

  const gridCols = plans.length === 3 ? "lg:grid-cols-3" : plans.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1";
  const myReplyLimit = replyCfg?.quota?.reply_limit;
  const myCompanyLimit = companyCfg?.quota?.company_limit;

  return (
    <div data-testid="pricing-page" className="space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight font-display text-slate-900">Plans &amp; Pricing</h1>
        <p className="text-slate-500 mt-2">Choose the plan that fits your outreach needs. Upgrade anytime as your applications grow.</p>
      </div>

      {/* Plan cards */}
      <div className={`grid gap-5 ${gridCols}`}>
        {plans.map((plan, i) => {
          const { amount, suffix } = priceLabel(plan);
          const featured = !!plan.badge;
          const isCurrent = current === plan.slug;
          const bullets = cardBullets(plan, i, plans, features);
          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.slug}`}
              className={`relative rounded-2xl border p-7 flex flex-col ${
                featured ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-200" : "border-slate-200 bg-white"
              }`}
            >
              {featured && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400 text-black text-xs font-bold shadow">
                  <Crown className="w-3.5 h-3.5" /> {plan.badge}
                </span>
              )}
              <h2 className="text-2xl font-bold text-slate-900 font-display">{plan.name}</h2>
              <p className="text-slate-500 text-sm mt-1">{plan.tagline}</p>

              <div className="mt-5 flex items-end gap-1.5">
                <span className="text-4xl font-bold text-slate-900 font-display">{amount}</span>
                {suffix && <span className="text-slate-500 text-sm mb-1.5">{suffix}</span>}
              </div>
              {plan.slug === "free" && <p className="text-sm text-slate-500 mt-1">{appsLabel(plan)}</p>}

              <Button
                data-testid={`plan-choose-${plan.slug}`}
                onClick={() => choose(plan)}
                disabled={busy === plan.id || isCurrent}
                className={`mt-5 h-11 font-semibold transition-colors ${
                  featured
                    ? "bg-amber-500 text-black hover:bg-amber-400"
                    : "bg-white border border-amber-400 text-amber-700 hover:bg-amber-50"
                }`}
              >
                {busy === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  isCurrent ? "Current Plan" : plan.slug === "free" ? "Get Started" : `Choose ${plan.name}`}
              </Button>

              <ul className="mt-6 space-y-2.5">
                {bullets.map((b, bi) => (
                  <li key={bi} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Reply credit top-up */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-100/50 p-7" data-testid="reply-topup-card">
        <h2 className="text-xl font-bold text-slate-900 font-display flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" /> Reply Credit Top-up
        </h2>
        <p className="text-slate-500 text-sm mt-1.5">
          {myReplyLimit == null
            ? "Your plan includes unlimited replies. Purchased top-up credits never expire and stay on your account if your plan ever changes."
            : `Your plan includes ${myReplyLimit ?? "…"} free replies each month. Need more? Buy extra reply credits — they never expire and are used only after your free replies run out.`}
        </p>
        <div className="mt-5 max-w-xl">
          <ReplyCreditPacks />
        </div>
      </div>

      {/* Company slot top-up */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-100/50 p-7" data-testid="company-topup-card">
        <h2 className="text-xl font-bold text-slate-900 font-display flex items-center gap-2">
          <Building2 className="w-5 h-5 text-amber-500" /> Company Slot Top-up
        </h2>
        <p className="text-slate-500 text-sm mt-1.5">
          {myCompanyLimit == null
            ? "Your plan includes unlimited company saves."
            : `Your plan lets you save up to ${myCompanyLimit ?? "…"} companies (lifetime — removing a company does not refund its credit). Need more? Buy extra company slots — they never expire.`}
        </p>
        <div className="mt-5 max-w-xl">
          <CompanySlotPacks />
        </div>
      </div>

      {/* Feature comparison */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="feature-comparison">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">Feature Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left border-b border-slate-100">
                <th className="px-6 py-3 font-semibold text-slate-600"></th>
                {plans.map((p) => (
                  <th key={p.id} className="px-4 py-3 text-center font-semibold text-slate-700">{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-50">
                <td className="px-6 py-3 text-slate-600">Applications</td>
                {plans.map((p) => (
                  <td key={p.id} className="px-4 py-3 text-center text-slate-700">
                    {p.application_limit == null ? "Unlimited" : p.billing_cycle === "monthly" ? `${p.application_limit} / month` : p.application_limit}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-slate-50" data-testid="reply-credits-row">
                <td className="px-6 py-3 text-slate-600">Reply credits</td>
                {plans.map((p) => (
                  <td key={p.id} className="px-4 py-3 text-center text-slate-700" data-testid={`reply-credits-${p.slug}`}>
                    {p.reply_limit == null ? "Unlimited" : `${p.reply_limit} / month`}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-slate-50" data-testid="company-slots-row">
                <td className="px-6 py-3 text-slate-600">Saved companies (lifetime)</td>
                {plans.map((p) => (
                  <td key={p.id} className="px-4 py-3 text-center text-slate-700" data-testid={`company-slots-${p.slug}`}>
                    {p.company_limit == null ? "Unlimited" : p.company_limit}
                  </td>
                ))}
              </tr>
              {features.map((f) => (
                <tr key={f.key} className="border-b border-slate-50">
                  <td className="px-6 py-3 text-slate-600">{f.label}</td>
                  {plans.map((p) => (
                    <td key={p.id} className="px-4 py-3 text-center">
                      {p.features[f.key]
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                        : <Minus className="w-4 h-4 text-slate-300 mx-auto" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
